<template>
  <v-dialog
    :model-value="modelValue"
    max-width="600"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="notes-modal">
      <v-card-title class="notes-modal__title">
        {{ modalTitle }}
      </v-card-title>

      <v-card-text class="notes-modal__content">
        <div class="notes-modal__patient-context">
          Call with <strong>{{ patientName }}</strong>
        </div>

        <div v-if="callWasAccepted" class="notes-modal__requirement-notice">
          <v-icon color="info" size="small" class="mr-2">mdi-information</v-icon>
          <span>This call was answered - notes are required for documentation</span>
        </div>
        <div v-else class="notes-modal__optional-notice">
          <v-icon color="grey" size="small" class="mr-2">mdi-information-outline</v-icon>
          <span>Optional: Add notes if needed</span>
        </div>

        <v-textarea
          v-model="notesText"
          variant="outlined"
          label="Call notes"
          placeholder="Enter notes about this call (e.g., patient concerns, follow-up actions, observations)"
          rows="6"
          counter="2000"
          :maxlength="2000"
          :error-messages="errorMessage"
          class="notes-modal__textarea mt-4"
        />

        <div class="notes-modal__hipaa-notice">
          <v-icon color="warning" size="small" class="mr-1">mdi-shield-alert</v-icon>
          <span class="text-caption">
            Do not include patient identifiable information (PHI) such as names, MRNs, or dates of birth
          </span>
        </div>
      </v-card-text>

      <v-card-actions class="notes-modal__actions">
        <v-btn
          v-if="!callWasAccepted"
          variant="text"
          color="grey"
          @click="handleSkip"
        >
          Skip
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="elevated"
          :disabled="callWasAccepted && notesText.trim().length === 0"
          :loading="isSaving"
          @click="handleSave"
        >
          {{ existingNotes ? 'Update Notes' : 'Save Notes' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

interface Props {
  modelValue: boolean
  callWasAccepted: boolean
  sessionId: string
  existingNotes?: string
  patientName: string
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'notes-saved', notes: string): void
  (e: 'notes-skipped'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const notesText = ref('')
const isSaving = ref(false)
const errorMessage = ref('')

const modalTitle = computed(() => {
  return props.existingNotes ? 'Edit Notes' : 'Add Notes'
})

// Initialize notes text when dialog opens
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    notesText.value = props.existingNotes || ''
    errorMessage.value = ''
    isSaving.value = false
  }
})

const handleSkip = () => {
  if (props.callWasAccepted) {
    errorMessage.value = 'Notes are required for calls that were answered'
    return
  }
  emit('notes-skipped')
  emit('update:modelValue', false)
}

const handleSave = async () => {
  if (props.callWasAccepted && notesText.value.trim().length === 0) {
    errorMessage.value = 'Please enter notes for this call'
    return
  }

  isSaving.value = true
  try {
    emit('notes-saved', notesText.value.trim())
    emit('update:modelValue', false)
  } catch (err) {
    errorMessage.value = 'Failed to save notes. Please try again.'
    isSaving.value = false
  }
}
</script>

<style scoped lang="scss">
.notes-modal {
  &__title {
    font-size: 20px;
    font-weight: 600;
    color: #291758;
    padding: 20px 24px 16px;
  }

  &__content {
    padding: 0 24px 16px;
  }

  &__patient-context {
    font-size: 14px;
    color: #666;
    margin-bottom: 12px;
    
    strong {
      color: #291758;
    }
  }

  &__requirement-notice {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background-color: rgba(33, 150, 243, 0.1);
    border-radius: 4px;
    font-size: 13px;
    color: #1976d2;
  }

  &__optional-notice {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background-color: rgba(158, 158, 158, 0.1);
    border-radius: 4px;
    font-size: 13px;
    color: #666;
  }

  &__textarea {
    margin-top: 16px;
  }

  &__hipaa-notice {
    display: flex;
    align-items: flex-start;
    padding: 8px 12px;
    background-color: rgba(255, 152, 0, 0.1);
    border-radius: 4px;
    margin-top: 8px;
    
    .text-caption {
      color: #f57c00;
      line-height: 1.4;
    }
  }

  &__actions {
    padding: 16px 24px 20px;
    border-top: 1px solid #e0e0e0;
  }
}
</style>
